<template>
  <div v-if="showLabel" :class="`articleLabel-container articleLabel-container--${mainComponent}`">
    <div :class="`${baseClasses} showShadow`" v-if="showShadow">{{ articleType }}</div>
    <div :class="`${baseClasses} animate__${articleType}`">{{ articleType }}</div>
  </div>
</template>

<script>
  import get from 'lodash/get'

  import { getArticleType } from 'mixins/utilities'
  import realtimeData from 'components/mixins/realtimeData'

  export default {
    name: 'speech-balloon-label',
    mixins: [realtimeData],
    props: {
      node: {
        type: Object,
        default: () => ({}),
      },
      size: { // for styling: label size
        type: String,
        default: 'medium',
      },
      mainComponent: { // for styling: input the main component so can customize per location
        type: String,
        default: 'default',
      },
      showShadow: {
        type: Boolean,
        default: false,
      },
    },
    mounted() {
      if (this.isLiveArticle) {
        this.initRealtimeData({modeType: 'live', nid: this.node.nid})
      }
    },
    computed: {
      articleType() {
        return getArticleType(this.node)
      },
      baseClasses() {
        return `articleLabel articleLabel__${this.articleType} font size--${this.size} pos pos--${this.mainComponent}`
      },
      isLiveArticle() {
        return this.articleType === 'live'
      },
      liveStatus() {
        if (this.isLiveArticle) {
          const status = get(this, 'node.live_contents.status', false)
          return status === 1
        }
        return false
      },
      showLabel() {
        return this.isLiveArticle ? this.liveStatus : true
      },
    },
  }
</script>

<style lang="scss" scoped>
  // MIXIN & FUNCTIONS
  @function checkValue($value, $allowance: 0) {
    @if $value == 'auto' {
      @return $value
    } @else {
      @return rem-calc($value + $allowance)
    }
  }

  @mixin label-position($top-mobile, $right-mobile, $left-mobile, $top-desktop: $top-mobile, $right-desktop: $right-mobile, $left-desktop: $left-mobile) {
    top: checkValue($top-mobile);
    right: checkValue($right-mobile);
    left: checkValue($left-mobile);
    @include respond-to('desktop') {
      top: checkValue($top-desktop);
      right: checkValue($right-desktop);
      left: checkValue($left-desktop);
    }

    &.showShadow {
      top: checkValue($top-mobile, 3);
      right: checkValue($right-mobile, -3);
      left: checkValue($left-mobile, 3);
      @include respond-to('desktop') {
        top: checkValue($top-desktop, 4);
        right: checkValue($right-desktop, -4);
        left: checkValue($left-desktop, 4);
      }
    }
  }


  // BASIC STYLING
  .articleLabel-container {
    height: 0;
    z-index: 1;
    position: relative;
    .article-view-wrapper .node-type-opinion & { // targeted parent and not container modifier since the label's mainComponent is shared among other components
      position: static;
    }
    &--article-cover-card {
      position: static;
    }
  }

  $base-height-rectangle: 35;
  .articleLabel {
    color: $white;
    padding: rem-calc(5 12 0 11);
    height: rem-calc($base-height-rectangle);
    min-width: rem-calc(77);
    @include create-speech-balloon($black, 11, 11)

    &__live {
      @include create-speech-balloon($red, 11, 11);

      // Dot
      &::before {
        content: '';
        display: inline-block;
        vertical-align: middle;
        background-color: $white;
        border-radius: 50%;

        height: rem-calc(7);
        width: rem-calc(7);
        margin-right: rem-calc(4);
        margin-top: rem-calc(-5);
      }

      &.showShadow {
        @include create-speech-balloon(rgba(0,0,0, 0.2), 11, 10, 0);

        // Dot
        &::before {
          background-color: transparent;
        }
      }
    }

    &__opinion {
      @include create-speech-balloon($black, 11, 11)
    }
  }

  .showShadow {
    color: transparent; //hide the text, so width can remain the same
  }

  .font {
    font-family: $barlow;
    font-weight: bold;
    text-transform: uppercase;
    white-space: nowrap;

    font-size: rem-calc(21); // based on $base-height-rectangle layout
    line-height: rem-calc(25);
  }


  // MODIFIERS
  .size {
    &--small {
      transform: scale(17/$base-height-rectangle);
    }

    &--small-responsive {
      @extend .size--small;
      @include respond-to('desktop') {
        transform: scale(27/$base-height-rectangle);
      }
    }

    &--small-20-responsive {
      transform: scale(20/$base-height-rectangle);
      @include respond-to('desktop') {
        transform: scale(34/$base-height-rectangle);
      }
    }

    &--medium {
      transform: scale(27/$base-height-rectangle);
    }

    &--large {
      transform: scale(35/$base-height-rectangle);
    }

    &--large-responsive {
      @extend .size--large;
      @include respond-to('desktop') {
        transform: scale(53/$base-height-rectangle);
      }
    }
  }

  .pos { // pos is based on mainComponent props
    position: absolute;
    z-index: 2;

    &.showShadow {
      position: absolute;
      z-index: 1;
    }

    &--article-cover-card {
      @include label-position(60, auto, 20, 93, auto, 170);
    }

    &--article-level-one {
      @include label-position(-4, auto, -14);

      &__node-label {
        @include label-position(-19, auto, -22, -24, auto, -2);
        position: relative;
      }
    }

    &--article-level-two {
      @include label-position(2, auto, -3);
      //   @include label-position(1, auto, 11);
    }

    &--article-level-six,
    &--article-level-seven {
      @include label-position(7, auto, 1);
    }

    &--article-level-eight {
      @include label-position(-8, -22, auto);
    }

    &--card-view {
      @include label-position(15, auto, -160);
    }

    &--body-head__node-label {
      @include label-position(0, 0, auto);
    }

    &--pushPopup {
      @include label-position(-4, auto, -15);
      @include respond-to('desktop') {
        @include label-position(1, auto, -4);
      }
    }
  }


  // ANIMATION
  .animate {
    &__live {
      animation: colorchangeBox 1s infinite;
      // Dot
      &::before {
        animation: colorchangeDot 1s infinite;
      }
      // Arrow
      &::after {
        animation: colorchangeArrow 1s infinite;
      }
    }
  }

  @keyframes colorchangeBox {
    0% { background-color: $red; }
    100% { background-color: $pastel-red; }
  }

  @keyframes colorchangeDot {
    0% { opacity: 0.6; }
    100% { opacity: 1; }
  }

  @keyframes colorchangeArrow {
    0% { border-top-color: $red;; }
    100% { border-top-color: $pastel-red; }
  }
</style>



// WEBPACK FOOTER //
// SpeechBalloonLabel.vue?5ef657dd